// index.ts — entry point for the class-summary bundle (IIFE → includes/js/dist/class-summary-bundle.js).
// A Step-3 per-object_type renderer in the object→JSON serializer initiative: render the class page's
// large summary TABLE, the class-info LIST, and the picker option lists client-side from the cached blob
// item.php already seeds into window.FateTide.objectCache. The PHP table_class_summary_inc.php fired a
// query_full_item_arr() per ability just to read name + requires_class_level; here every ability/spell
// resolves from the blob's root `.catalog` — ZERO server queries for the page item. Picking a subclass
// fetches ONE blob (options/api/object_inc.php via fetchAndCacheObject) and re-renders client-side; no
// server table rebuild.
//
// MOUNT CONTRACT — table_item_class_inc.php emits (desktop-only summary table):
//   <div id="div_refresh_class_summary_table" class="d-none d-lg-block …"
//        data-ft-class-summary data-ft-class-id="<class uuid>" [data-ft-subclass-id="<subclass uuid>"]></div>
//   <div data-ft-class-info data-ft-class-id="<class uuid>"></div>

import { renderClassTableHtml, collectAbilities, buildNames } from './build-table';
import { renderClassInfoHtml } from './class-info';
import { applySearch } from './search';
import { mountPickers } from './pickers';
import { itemsCol } from '../shared/array-types';
import type { AbilityRow } from './types';
import { classBlobOf, type ClassBlob } from './blob';

const SUMMARY_SELECTOR = '[data-ft-class-summary]';
const INFO_SELECTOR = '[data-ft-class-info]';
const DONE_ATTR = 'data-ft-cs-done';
const PRIMARY_ID = 'div_refresh_class_summary_table';

interface Instance {
    el: HTMLElement;
    classId: string;
    subclassId: string;
    searchTerm: string;
}

const instances = new Map<HTMLElement, Instance>();
/** The interactive page table (the one the subclass picker drives), by its stable id. */
let primary: Instance | null = null;
let pickersMounted = false;

function getObject(id: string): ClassBlob | null {
    return id ? classBlobOf(window.FateTide?.objectCache?.get(id)) : null;
}

/** Warm cache first; cold-fetch the object blob (websocket bundle) on a miss. */
async function loadObject(id: string): Promise<ClassBlob | null> {
    if (!id) return null;
    let o = getObject(id);
    if (!o && typeof fetchAndCacheObject === 'function') {
        await fetchAndCacheObject(id);
        o = getObject(id);
    }
    return o;
}

/** (Re)render an instance's table from class + optional subclass blob, preserving the live search term. */
function renderInstance(inst: Instance, classBlob: ClassBlob, subclassBlob: ClassBlob | null): void {
    const names = buildNames(classBlob, subclassBlob);
    const rows: AbilityRow[] = collectAbilities(classBlob, 'class', names);
    if (subclassBlob) rows.push(...collectAbilities(subclassBlob, 'subclass', names));

    const className = String(itemsCol(classBlob, 'item_name') ?? '');
    inst.el.innerHTML = renderClassTableHtml({ classId: inst.classId, className, rows, searchTerm: inst.searchTerm });

    const input = inst.el.querySelector<HTMLInputElement>('.ft-cs-search-input');
    if (input) {
        input.addEventListener('input', () => {
            inst.searchTerm = input.value;
            applySearch(inst.el, inst.searchTerm);
        });
    }
    applySearch(inst.el, inst.searchTerm);
}

async function paintSummary(el: HTMLElement): Promise<void> {
    const classId = el.getAttribute('data-ft-class-id') || '';
    if (!classId) return;
    const subclassId = el.getAttribute('data-ft-subclass-id') || '';

    const classBlob = await loadObject(classId);
    if (!classBlob) return; // leave un-done for a later re-scan / next cache fill.

    let inst = instances.get(el);
    if (!inst) { inst = { el, classId, subclassId, searchTerm: '' }; instances.set(el, inst); }
    else { inst.classId = classId; inst.subclassId = subclassId; }

    const subclassBlob = subclassId ? await loadObject(subclassId) : null;
    renderInstance(inst, classBlob, subclassBlob);
    el.setAttribute(DONE_ATTR, '1');
    if (el.id === PRIMARY_ID) primary = inst;
}

async function paintInfo(el: HTMLElement): Promise<void> {
    const classId = el.getAttribute('data-ft-class-id') || '';
    if (!classId) return;
    const classBlob = await loadObject(classId);
    if (!classBlob) return;
    el.innerHTML = renderClassInfoHtml(classBlob);
    el.setAttribute(DONE_ATTR, '1');
}

/** Scan a root for un-mounted class-summary / class-info mounts and paint them; mount pickers once. */
function ftMountClassSummary(root: ParentNode = document): void {
    root.querySelectorAll<HTMLElement>(`${SUMMARY_SELECTOR}:not([${DONE_ATTR}="1"])`).forEach(el => { void paintSummary(el); });
    root.querySelectorAll<HTMLElement>(`${INFO_SELECTOR}:not([${DONE_ATTR}="1"])`).forEach(el => { void paintInfo(el); });
    if (!pickersMounted && document.getElementById('input_class_form_select')) {
        mountPickers();
        pickersMounted = true;
    }
}

/**
 * Re-render the interactive page table for a newly picked subclass (or '' to revert to class-only).
 * Called from template.js querySubclassTable — operates on the EXISTING mounted instance (no re-scan),
 * so it never races the card AJAX. Fetches just the one subclass blob.
 */
async function ftClassSummarySetSubclass(subclassId: string): Promise<void> {
    let inst = primary;
    if (!inst) {
        const el = document.getElementById(PRIMARY_ID);
        if (el) inst = instances.get(el) ?? null;
    }
    if (!inst) return;

    inst.subclassId = subclassId || '';
    if (subclassId) inst.el.setAttribute('data-ft-subclass-id', subclassId);
    else inst.el.removeAttribute('data-ft-subclass-id');

    const classBlob = await loadObject(inst.classId);
    if (!classBlob) return;
    const subclassBlob = subclassId ? await loadObject(subclassId) : null;
    renderInstance(inst, classBlob, subclassBlob);
}

Object.assign(window, { ftMountClassSummary, ftClassSummarySetSubclass });

// Page-load mount once the DOM is ready (no-op on pages with no mount). AJAX surfaces (openItemInfo) call
// window.ftMountClassSummary() explicitly after injection.
(function ftInitClassSummary(): void {
    ftMountClassSummary();
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => ftMountClassSummary());
    }
})();
